import React, { useEffect, useMemo } from "react";
import Carousel from "react-multi-carousel";
import { Link } from "react-router-dom";
import "react-multi-carousel/lib/styles.css";
import { useDispatch, useSelector } from "react-redux";
import { get_banners } from "../store/reducers/homeReducer";

// Constantes para aspect ratios según tus breakpoints
const ASPECT_RATIOS = {
  DEFAULT: 26.84, // 500/1200 * 100 ≈ 41.67% (para desktop)
  DESKTOP: 26.84, // 500/1200 * 100
  MOBILE: 93.75,  // 720/768 * 100 ≈ 93.75% (para móvil)
};

// Componentes memoizados para las flechas
const CustomRightArrow = React.memo(({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-2 md:right-4 bg-white/90 hover:bg-white text-gray-800 rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-center shadow-lg transition-all duration-300 z-10 focus:outline-none focus:ring-2 focus:ring-primary hover:scale-105 active:scale-95"
    aria-label="Siguiente banner"
    type="button"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4 md:h-5 md:w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5l7 7-7 7"
      />
    </svg>
  </button>
));

const CustomLeftArrow = React.memo(({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-2 md:left-4 bg-white/90 hover:bg-white text-gray-800 rounded-full w-8 h-8 md:w-10 md:h-10 flex items-center justify-center shadow-lg transition-all duration-300 z-10 focus:outline-none focus:ring-2 focus:ring-primary hover:scale-105 active:scale-95"
    aria-label="Anterior banner"
    type="button"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4 md:h-5 md:h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 19l-7-7 7-7"
      />
    </svg>
  </button>
));

// Componente para los dots con responsive
const CustomDot = React.memo(({ onClick, active, index }) => (
  <button
    className={`mx-0.5 sm:mx-1 transition-all duration-300 focus:outline-none rounded-full ${
      active 
        ? "w-5 sm:w-8 md:w-10 h-1.5 sm:h-2 bg-white rounded-sm" 
        : "w-1.5 sm:w-2 h-1.5 sm:h-2 bg-white/50 hover:bg-white/70 rounded-full"
    }`}
    onClick={onClick}
    aria-label={`Ir al banner ${index + 1}`}
    type="button"
  />
));

const Banner = () => {
  const dispatch = useDispatch();
  const { banners } = useSelector((state) => state.home);
  const bannersLoaded = banners.length > 0;

  // Configuración responsive adaptada a tus breakpoints
// Configuración responsive adaptada a tus breakpoints
const responsive = useMemo(() => ({
  // Para pantallas muy grandes (mayores a 1200px)
  superLargeDesktop: {
    breakpoint: { max: 4000, min: 1201 },
    items: 1,
  },
  // Escritorio grande: 981px a 1200px
  desktop: {
    breakpoint: { max: 1200, min: 981 },
    items: 1,
  },
  // Tablet grande/pequeño escritorio: 769px a 980px
  largeTablet: {
    breakpoint: { max: 980, min: 769 },
    items: 1,
  },
  // Tablet: 641px a 768px
  tablet: {
    breakpoint: { max: 768, min: 641 },
    items: 1,
  },
  // Móvil grande: 481px a 640px
  mobile: {
    breakpoint: { max: 640, min: 481 },
    items: 1,
  },
  // Móvil pequeño: 361px a 480px
  smallMobile: {
    breakpoint: { max: 480, min: 361 },
    items: 1,
  },
  // Móvil muy pequeño: 0px a 360px
  extraSmallMobile: {
    breakpoint: { max: 360, min: 0 },
    items: 1,
  },
}), []);

  // Configuración del carousel con ajustes responsive
  const carouselConfig = useMemo(() => ({
    autoPlay: true,
    infinite: true,
    arrows: true,
    showDots: true,
    responsive,
    customRightArrow: <CustomRightArrow />,
    customLeftArrow: <CustomLeftArrow />,
    customDot: <CustomDot />,
    autoPlaySpeed: 6000,
    containerClass: "carousel-container m-0 p-0",
    sliderClass: "h-full m-0 p-0",
    itemClass: "h-full m-0 p-0",
    dotListClass: "absolute bottom-1.5 sm:bottom-3 md:bottom-4 lg:bottom-5 flex justify-center items-center w-full m-0 p-0",
    keyBoardControl: true,
    swipeable: true,
    draggable: true,
    additionalTransfrom: 0,
    shouldResetAutoplay: true,
  }), [responsive]);

  useEffect(() => {
    dispatch(get_banners());
  }, [dispatch]);

  // Estado de carga skeleton
  if (!bannersLoaded) {
    return (
      <div className="w-full mx-0 my-0 p-0">
        <div className="relative overflow-hidden shadow-lg rounded-lg bg-gray-100 animate-pulse">
          <div 
            className="w-full h-0"
            style={{ paddingBottom: `${ASPECT_RATIOS.DEFAULT}%` }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-gray-400">
                <svg className="w-12 h-12 animate-spin text-gray-300" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Si no hay banners
  if (banners.length === 0) {
    return (
      <div className="w-full mx-0 my-0 p-0">
        <div className="relative overflow-hidden shadow-lg rounded-lg bg-gradient-to-r from-gray-100 to-gray-200">
          <div 
            className="w-full h-0"
            style={{ paddingBottom: `${ASPECT_RATIOS.DEFAULT}%` }}
          >
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
              <svg className="w-16 h-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-700 mb-2">No hay banners disponibles</h3>
              <p className="text-gray-500 text-sm">Pronto tendremos nuevas ofertas</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-0 my-0 p-0">
      <div className="w-full">
        <div className="relative overflow-hidden shadow-lg rounded-lg">
          <Carousel {...carouselConfig}>
            {banners.map((banner, index) => (
              <div key={`banner-${banner.id || banner.link || index}`} className="h-full">
                <Link
                  to={`/product/details/${banner.link}`}
                  className="block w-full h-full group"
                  aria-label={`Ver ${banner.name || 'producto promocionado'}`}
                >
                  {/* Contenedor con aspect ratio responsive */}
                  <div className="relative overflow-hidden">
                            
                    {/* Contenedor para MÓVIL (hasta 768px) */}
                    <div className="md:block hidden">
                      <div 
                        className="w-full h-0"
                        style={{ paddingBottom: `${ASPECT_RATIOS.MOBILE}%` }}
                      >
                        <img
                          src={banner.banner_movil || banner.banner}
                          alt={banner.alt || `Promoción ${index + 1}`}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading={index === 0 ? "eager" : "lazy"}
                          decoding="async"
                          sizes="100vw"
                        />
                      </div>
                    </div>
                    
                    {/* Contenedor para DESKTOP (mayor a 768px) */}
                    <div className="md:hidden block">
                      <div    className="w-full h-0"  
                        style={{ paddingBottom: `${ASPECT_RATIOS.DESKTOP}%` }}>
                        <img
                          src={banner.banner}
                          alt={banner.alt || `Promoción ${index + 1}`}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading={index === 0 ? "eager" : "lazy"}
                          decoding="async"
                          sizes="100vw"
                        />
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    </div>
  );
};

// Componente optimizado con memo
export default React.memo(Banner);